import { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Mail, CheckCircle2, AlertTriangle, Loader2, MailCheck, PhoneCall } from "lucide-react";
import { api } from "../../lib/api";
import { leadAttribution } from "../../lib/attribution";
import { Section, Field, RetryImage } from "./ui";
import ShareButtons from "../../components/ShareButtons";
import { LinkQuoteCompareModal } from "./LinkQuoteCompareModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KIND_LABEL = { exterior: "Exterior", interior: "Interior", bathroom: "Bathroom / WC", kitchen: "Kitchen" };

// One AI visual tile. Its image is only mounted once the backend reports the render as ready
// (status-driven — no blind 404 polling), then retried briefly to cover storage propagation lag.
function Visual({ qid, kind, ready, stalled, slow, testid }) {
  const [loaded, setLoaded] = useState(false);
  const label = KIND_LABEL[kind] || kind;
  if (stalled && !ready) {
    return (
      <figure className="relative border border-[#E5E7EB] bg-[#F1F3F5]">
        <div className="aspect-square flex flex-col items-center justify-center text-center px-6" data-testid={`${testid}-stalled`}>
          <MailCheck size={22} className="text-[#C79A3B]" />
          <span className="text-xs font-bold mt-3 text-[#12263A]">Your {label.toLowerCase()} visual is still being prepared</span>
          <span className="text-[11px] mt-2 leading-relaxed text-[#6B7280]">We'll email it to you and save it in your client portal as soon as it's ready — no need to wait here.</span>
        </div>
        <figcaption className="absolute top-3 left-3 z-20 bg-[#12263A]/90 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">{label}</figcaption>
      </figure>
    );
  }
  return (
    <figure className="relative border border-[#E5E7EB] bg-[#F1F3F5] overflow-hidden">
      {!(ready && loaded) && (
        <div className="aspect-square flex flex-col items-center justify-center text-center text-[#9CA3AF] px-6">
          <Loader2 size={22} className="animate-spin" />
          <span className="text-[11px] mt-2 uppercase tracking-wider">Rendering {label.toLowerCase()}…</span>
          {slow && (
            <span className="text-[11px] mt-3 normal-case tracking-normal leading-relaxed text-[#6B7280]" data-testid={`${testid}-slow`}>
              This is taking a little longer than usual. We've also emailed your visuals and saved them to your portal — the image will appear here automatically as soon as it's ready.
            </span>
          )}
        </div>
      )}
      {ready && (
        <RetryImage src={`${BACKEND}/api/public/pd-render/${qid}/${kind}`} alt={`Your extension — ${label.toLowerCase()} visual`}
          className={`w-full h-auto ${loaded ? "block" : "hidden"}`}
          testid={testid} maxTries={12} interval={3000} onLoad={() => setLoaded(true)} />
      )}
      <figcaption className="absolute top-3 left-3 z-20 bg-[#12263A]/90 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">{label}</figcaption>
    </figure>
  );
}

export function QuoteRevealStep({ config, site, route, postcode, quoteResult, pdQuote, setPdQuote, contact, setContact, settings, propType, pdDepth, editingQuoteId, enquiryId, linkBooking, onBack, onNext, isStaff, adminNote, onAdminNoteFlushed }) {
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(null); // { preview, quote_id } — shown when a swap needs confirmation
  const [linkBusy, setLinkBusy] = useState(false);
  const sig = useMemo(() => JSON.stringify({ config, site }), [config, site]);
  // The reveal is only "current" when the saved quote matches the design on screen.
  const upToDate = !!(pdQuote?.quote_id && pdQuote?.sig && pdQuote.sig === sig);
  const liveTotal = Math.round(quoteResult?.total_inc_vat || pdQuote?.total_inc_vat || 0);

  const [shareUrl, setShareUrl] = useState("");
  useEffect(() => {
    if (!(pdQuote?.quote_id && upToDate)) { setShareUrl(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/quote/share", { data: { config, site, route, propType, pdDepth } });
        if (!cancelled) setShareUrl(`${window.location.origin}/api/s/${r.data.id}`);
      } catch (e) { console.debug("Share link unavailable (non-blocking):", e); }
    })();
    return () => { cancelled = true; };
  }, [upToDate, sig]); // eslint-disable-line react-hooks/exhaustive-deps -- refresh the share link when the current saved design changes

  // Status-driven render polling: ask the backend which visuals exist, self-heal missing ones
  // (server-side retry) and stop gracefully instead of spinning forever.
  const [renderStatus, setRenderStatus] = useState(null);
  const [renderSlow, setRenderSlow] = useState(false);
  const [renderStalled, setRenderStalled] = useState(false);
  useEffect(() => {
    if (!(pdQuote?.quote_id && upToDate)) return;
    let stop = false, polls = 0;
    setRenderSlow(false); setRenderStalled(false);
    const qid = pdQuote.quote_id;
    const tick = async () => {
      if (stop) return;
      polls += 1;
      try {
        const r = await api.get(`/quote/pd/${qid}/render-status`);
        if (stop) return;
        setRenderStatus(r.data);
        const allReady = (r.data.expected || []).every((k) => r.data.ready?.[k]);
        if (allReady) return;
        if (polls === 7) setRenderSlow(true);
        if (polls === 9 || polls === 25) api.post(`/quote/pd/${qid}/render-retry`).catch(() => {});
        if (polls >= 48) { setRenderStalled(true); return; }
      } catch (e) { console.debug("Render status poll failed (non-blocking):", e); }
      if (!stop) setTimeout(tick, 5000);
    };
    tick();
    return () => { stop = true; };
  }, [pdQuote?.quote_id, upToDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!contact.email || !contact.name || !contact.phone) { toast.error("Your name, email and phone number are required to see your price."); return; }
    setBusy(true);
    try {
      const r = await api.post("/quote/save", {
        ...config, site, route: "pd", postcode,
        name: contact.name, email: contact.email, phone: contact.phone || "",
        quote_id: pdQuote?.quote_id || editingQuoteId || undefined,
        enquiry_id: enquiryId || undefined,
        ...leadAttribution(),
      });
      setPdQuote({ ...r.data, sig });
      toast.success(r.data.emailed ? "Quote saved & emailed to you 🎉"
        : (r.data.staff_suppressed_email ? "Quote saved — client NOT emailed (staff mode). Send it manually when ready." : "Quote saved"));
      // Flush the staff's private draft note (if any) onto the freshly-saved quote as a
      // proper threaded note — visible in Quote → Notes tab. Best-effort: never blocks the save.
      if (isStaff && adminNote && adminNote.trim() && r.data.quote_id) {
        try {
          await api.post(`/admin/quote/${encodeURIComponent(r.data.quote_id)}/note`, { body: adminNote.trim() });
          if (onAdminNoteFlushed) onAdminNoteFlushed();
        } catch (e) {
          console.debug("Admin note flush failed (non-blocking):", e);
          toast.error("Quote saved but the private note couldn't be attached — copy it and add it manually from the Notes tab.");
        }
      }
      // Staff-triggered auto-link: when the office opens the configurator from a
      // Job File via ?link_booking=<ref>, sync the resulting quote back to that
      // booking so `quote_id`, `total_inc_vat` and pricing_breakdown stay in sync.
      // If a DIFFERENT quote is already linked to the booking, show a side-by-side
      // compare modal so the office can spot mistakes before committing the swap.
      // Best-effort — needs an admin session (cookie); silently ignored otherwise.
      if (linkBooking && r.data.quote_id) {
        try {
          const prev = await api.post(`/admin/booking/${encodeURIComponent(linkBooking)}/link-quote-preview`, {
            quote_id: r.data.quote_id,
          });
          if (prev?.data?.needs_confirm) {
            // Defer the link — user must confirm the swap.
            setCompare({ preview: prev.data, quote_id: r.data.quote_id });
          } else {
            await commitLink(r.data.quote_id);
          }
        } catch (e) {
          console.debug("Auto-link preview failed (non-blocking):", e);
          toast.error("Quote saved but the booking auto-link needs admin sign-in — link it manually from the Job File.");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your quote");
    }
    setBusy(false);
  };

  const commitLink = async (quote_id) => {
    setLinkBusy(true);
    try {
      const lk = await api.post(`/admin/booking/${encodeURIComponent(linkBooking)}/link-quote`, { quote_id });
      if (lk?.data?.ok) {
        toast.success(`Job ${linkBooking} now linked to this quote — totals synced (${lk.data.total_inc_vat ? "£" + Math.round(lk.data.total_inc_vat).toLocaleString("en-GB") : ""}).${lk.data.previous_archived ? " Previous quote archived (restorable from Activity)." : ""}`);
      }
      setCompare(null);
    } catch (e) {
      console.debug("Link-quote failed:", e);
      toast.error(e?.response?.data?.detail || "Could not link the quote to the booking");
    }
    setLinkBusy(false);
  };

  const linkModal = (
    <LinkQuoteCompareModal preview={compare?.preview} busy={linkBusy}
      onConfirm={() => commitLink(compare?.quote_id)}
      onCancel={() => { setCompare(null); toast.info("Booking not updated — quote saved but not linked."); }} />
  );

  // 1. No saved quote yet — capture name+email+phone, then reveal & email on submit.
  if (!pdQuote?.quote_id) {
    return (
      <>
      <Section title="See your price & book online" eyebrow="Step 5 · Your quote">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-4xl">
          <div>
            {(contact.name && contact.email && contact.phone) ? (
              <>
                <p className="text-[#6B7280] mb-6">Click below to reveal your itemised price, save it under your own reference, and email you a copy — so you can book online, think it over, share it, or pick up exactly where you left off.</p>
                <div className="text-sm text-[#374151] mb-6 space-y-1 border border-[#E5E7EB] bg-white p-4" data-testid="qr-contact-summary">
                  <div><span className="text-[#6B7280]">Name:</span> <b>{contact.name}</b></div>
                  <div><span className="text-[#6B7280]">Email:</span> <b>{contact.email}</b></div>
                  <div><span className="text-[#6B7280]">Phone:</span> <b>{contact.phone}</b></div>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#6B7280] mb-6">Pop in your name, email and phone number and we'll reveal your itemised price, save it under your own reference, and email you a copy — so you can book online, think it over, share it, or pick up exactly where you left off.</p>
                <Field label="Full name" v={contact.name} on={v => setContact({ ...contact, name: v })} testid="qr-name" />
                <Field label="Email" type="email" v={contact.email} on={v => setContact({ ...contact, email: v })} testid="qr-email" />
                <Field label="Phone number" type="tel" v={contact.phone} on={v => setContact({ ...contact, phone: v })} testid="qr-phone" />
              </>
            )}
            <div className="mt-6 flex gap-3">
              <button data-testid="qr-back" className="de-btn-secondary" onClick={onBack}><ArrowLeft size={16} /> Edit design</button>
              <button data-testid="qr-reveal" disabled={busy} className="de-btn-primary" onClick={save}>{busy ? "Preparing your quote…" : (<>See my price &amp; book online <ArrowRight size={16} /></>)}</button>
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-3">We'll only use your details to send your quote and follow up about your project. No spam.</p>
          </div>
          <div className="bg-[#12263A] text-white p-6 self-start">
            <div className="text-xs uppercase tracking-widest text-[#C79A3B]">Your estimated total (incl. VAT)</div>
            <div className="text-4xl font-black mt-1 select-none tracking-widest" data-testid="qr-blurred-total">£•••,•••</div>
            <div className="text-sm text-white/70 mt-3">Enter your details to reveal the full breakdown, your 3D visual and your saved reference — then book your date online.</div>
          </div>
        </div>
      </Section>
      {linkModal}
      </>
    );
  }

  // 2. A saved quote exists but the design changed (or was reopened to edit) — DON'T auto-reveal
  //    or re-send. Re-gate behind a single explicit "see my (updated) price" click.
  if (!upToDate) {
    const reopened = !!pdQuote?.sig; // had a sig already => this is an edit to an already-revealed quote
    const cta = reopened ? "See my updated price" : "See my price";
    return (
      <>
      <Section title={reopened ? "You've updated your design" : "See your price & book online"} eyebrow="Step 5 · Your quote">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-4xl">
          <div>
            <p className="text-[#6B7280] mb-6">{reopened
              ? "Your design has changed since your last quote. Click below to see your updated price — we'll refresh your saved quote (same reference) and email you the new copy. Nothing is sent until you click."
              : "Click below to reveal your itemised price under your saved reference, email you a copy, and book your start date online."}</p>
            <div className="text-sm text-[#374151] mb-6 space-y-1 border border-[#E5E7EB] bg-white p-4">
              <div><span className="text-[#6B7280]">Name:</span> <b>{contact.name}</b></div>
              <div><span className="text-[#6B7280]">Email:</span> <b>{contact.email}</b></div>
              <div><span className="text-[#6B7280]">Phone:</span> <b>{contact.phone}</b></div>
            </div>
            <div className="mt-2 flex gap-3">
              <button data-testid="qr-back" className="de-btn-secondary" onClick={onBack}><ArrowLeft size={16} /> Edit design</button>
              <button data-testid="qr-reveal" disabled={busy} className="de-btn-primary" onClick={save}>{busy ? "Preparing your quote…" : (<>{cta} &amp; book online <ArrowRight size={16} /></>)}</button>
            </div>
          </div>
          <div className="bg-[#12263A] text-white p-6 self-start">
            <div className="text-xs uppercase tracking-widest text-[#C79A3B]">Your estimated total (incl. VAT)</div>
            <div className="text-4xl font-black mt-1 select-none tracking-widest" data-testid="qr-blurred-total">£•••,•••</div>
            <div className="text-sm text-white/70 mt-3">Click "{cta} &amp; book online" to reveal your full breakdown and book your date.</div>
          </div>
        </div>
      </Section>
      {linkModal}
      </>
    );
  }

  // 3. Quote saved & current — full reveal with all applicable AI visuals.
  const expectedKinds = renderStatus?.expected || pdQuote?.render_kinds || ["exterior", "interior"];
  const readyMap = renderStatus?.ready || {};
  const testidOf = { exterior: "qr-render", interior: "qr-render-interior", bathroom: "qr-render-bathroom", kitchen: "qr-render-kitchen" };

  const totalBefore = Math.round(quoteResult?.total_before_discount ?? pdQuote?.total_before_discount ?? 0);
  const discountAmt = Math.round(quoteResult?.discount_amount ?? pdQuote?.discount_amount ?? 0);
  const discountLabel = quoteResult?.discount_label || pdQuote?.discount_label || "";
  const depositPct = settings?.deposit_pct ?? 0.10;
  const bookingFeeCap = settings?.booking_fee ?? 499;
  const fullDeposit = Math.max(Math.round(liveTotal * depositPct), bookingFeeCap);
  const bookingFee = Math.min(bookingFeeCap, fullDeposit);
  const depositBalance = Math.max(0, fullDeposit - bookingFee);
  const included = pdQuote?.included || quoteResult?.included_list || [];
  const notIncluded = pdQuote?.not_included || [];

  return (
    <>
    <Section title="Here's your quote" eyebrow="Step 5 · Your quote">
      {/* AI visuals — status-driven, shown in full (no cropping) */}
      <div className="max-w-3xl mb-10">
        <div className="label-eyebrow mb-3">Your extension, visualised</div>
        <div className={`grid gap-4 ${expectedKinds.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
          {expectedKinds.map((kind) => (
            <Visual key={kind} qid={pdQuote.quote_id} kind={kind} ready={!!readyMap[kind]}
              stalled={renderStalled} slow={renderSlow} testid={testidOf[kind] || `qr-render-${kind}`} />
          ))}
        </div>
        <div className="text-[11px] text-[#9CA3AF] mt-3">AI-generated visuals matched to your design{expectedKinds.includes("bathroom") || expectedKinds.includes("kitchen") ? ", including the rooms you've chosen" : ""}. Final layout, door, window &amp; rooflight positions are confirmed at your site survey.</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl">
        <div>
          <div className="border border-[#E5E7EB] bg-white p-5">
            <div className="label-eyebrow">Your quote reference</div>
            <div className="text-2xl font-black text-[#C79A3B]" data-testid="qr-reference">{pdQuote.reference}</div>
            <div className="text-xs text-[#6B7280] mt-1">Valid until <b>{pdQuote.valid_until ? new Date(pdQuote.valid_until).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "—"}</b></div>
          </div>
          <div className="bg-[#12263A] text-white p-6 mt-4">
            <div className="text-xs uppercase tracking-widest text-[#C79A3B]">Total (incl. VAT)</div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="text-4xl font-black mt-1" data-testid="qr-total">£{liveTotal.toLocaleString("en-GB")}</div>
              {discountAmt > 0 && (
                <div className="text-lg text-white/50 line-through" data-testid="qr-total-before">£{totalBefore.toLocaleString("en-GB")}</div>
              )}
            </div>
            {discountAmt > 0 && (
              <div className="mt-3 bg-[#C79A3B] text-[#12263A] px-3 py-2 text-sm font-bold leading-snug" data-testid="qr-discount-highlight">
                You save £{discountAmt.toLocaleString("en-GB")} — {discountLabel}
              </div>
            )}
            {settings?.site_visit_mode ? (
              <>
                <div className="mt-3 text-sm text-white/90 leading-relaxed" data-testid="qr-deposit-line">
                  <b className="text-[#C79A3B]">Nothing to pay to book.</b> Book a <b>free 1-hour site visit</b> — we&rsquo;ll measure up and talk through your design and quote.
                </div>
                <div className="text-xs text-white/70 mt-1.5" data-testid="qr-deposit-after-visit">
                  Your {Math.round(depositPct * 100)}% deposit (<b className="text-white">£{fullDeposit.toLocaleString("en-GB")}</b>) is only paid <b className="text-white">after the visit</b> &mdash; and only if you decide to go ahead. We agree your start date at the same time. Nothing is taken when you book.
                </div>
              </>
            ) : (
              <>
                <div className="mt-3 text-sm text-white/90 leading-relaxed" data-testid="qr-deposit-line">
                  Lock in this price &amp; your start date today with a <b>£{bookingFee.toLocaleString("en-GB")}</b> booking fee <span className="text-[#C79A3B] font-bold whitespace-nowrap">(fully refundable within 14 days*)</span>
                </div>
                {depositBalance > 0 && (
                  <div className="text-xs text-white/70 mt-1.5" data-testid="qr-deposit-balance-line">
                    It comes off your 10% deposit &mdash; the balance of <b>£{depositBalance.toLocaleString("en-GB")}</b> is due after your on-site survey is completed &amp; signed off.
                  </div>
                )}
                <div className="text-[10px] text-white/50 mt-1.5">*Your £{bookingFee.toLocaleString("en-GB")} booking fee is fully refundable if you cancel within 14 days.</div>
              </>
            )}
            <div className="text-xs text-white/70 mt-3 flex items-center gap-1.5"><Mail size={13} /> Emailed to {contact.email}. Sign in to your portal anytime with this reference + email.</div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button data-testid="qr-edit" className="de-btn-secondary" onClick={onBack}><ArrowLeft size={16} /> Edit design</button>
            <button data-testid="qr-book" className="de-btn-primary" onClick={onNext}>{settings?.site_visit_mode ? "Book my free site visit" : "Book a date & pay £499"} <ArrowRight size={16} /></button>
          </div>
          <CallbackRequestCard contact={contact} quote={pdQuote} total={liveTotal} postcode={postcode} />
        </div>
        <div>
          <div className="label-eyebrow mb-2">Your design</div>
          <ul className="space-y-2 mb-6" data-testid="qr-spec">
            {(pdQuote.spec_lines || []).map((l, i) => (
              <li key={`${i}-${l}`} className="flex gap-2 text-sm text-[#374151]"><CheckCircle2 size={16} className="text-[#C79A3B] mt-0.5 shrink-0" /><span>{l}</span></li>
            ))}
          </ul>
          {shareUrl && (
            <div className="de-card p-4" data-testid="qr-share">
              <div className="font-bold text-sm mb-1">Share your design</div>
              <p className="text-xs text-[#6B7280] mb-3">Send it to a partner for a second opinion — they can open and tweak it too.</p>
              <ShareButtons url={shareUrl} text="Here's the extension I designed with The Extension Works:" testidPrefix="qr-share" />
            </div>
          )}
        </div>
      </div>

      {/* Included / not-included side by side */}
      <div className="mt-12 max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        <div className="border border-[#E5E7EB] bg-white p-6" data-testid="qr-included">
          <div className="label-eyebrow mb-1">What's included in your build</div>
          <p className="text-xs text-[#6B7280] mb-4">Handed over fully finished and <b className="text-[#12263A]">ready to use from day one</b> — second-fix electrics live, walls &amp; ceilings plastered and white-washed, flooring fitted, skirting on.</p>
          <ul className="space-y-2">
            {included.map((l, i) => (
              <li key={`${i}-${l}`} className="flex gap-2 text-sm text-[#374151]"><CheckCircle2 size={16} className="text-[#1F6B3B] mt-0.5 shrink-0" /><span>{l}</span></li>
            ))}
          </ul>
        </div>
        <div className="border border-[#E5E7EB] bg-[#FBFAF8] p-6" data-testid="qr-not-included">
          <div className="label-eyebrow mb-1 flex items-center gap-1.5"><AlertTriangle size={14} className="text-[#C79A3B]" /> The few things not included</div>
          <p className="text-xs text-[#6B7280] mb-4">Mostly items you choose yourself, or rare site conditions we can't see until survey.</p>
          <ul className="space-y-2">
            {notIncluded.map((l, i) => (
              <li key={`${i}-${l}`} className="flex gap-2 text-sm text-[#6B7280]"><span className="text-[#C79A3B] font-bold mt-0.5 shrink-0">•</span><span>{l}</span></li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
    <MobileBookBar total={liveTotal} bookingFee={bookingFee} siteVisit={!!settings?.site_visit_mode} onNext={onNext} />
    {linkModal}
    </>
  );
}

// Mobile-only sticky bar: keeps the price + "Book" CTA one tap away while the visitor
// scrolls the long reveal page. Hides itself while the main price panel is on screen.
function MobileBookBar({ total, bookingFee, siteVisit, onNext }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = document.querySelector('[data-testid="qr-total"]');
    if (!el || typeof IntersectionObserver === "undefined") { setShow(true); return; }
    const io = new IntersectionObserver(([e]) => setShow(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  if (!show) return null;
  return (
    <>
    <div className="lg:hidden h-16" aria-hidden="true" />
    <div data-testid="qr-mobile-book-bar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#12263A] text-white px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-6px_20px_rgba(0,0,0,0.18)]">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#C79A3B] font-bold">Your price · incl. VAT</div>
        <div className="text-xl font-black leading-tight" data-testid="qr-mobile-total">£{total.toLocaleString("en-GB")}</div>
      </div>
      <button data-testid="qr-mobile-book" onClick={onNext} className="de-btn-primary whitespace-nowrap !px-5">
        {siteVisit ? "Book free site visit" : `Book · pay £${bookingFee}`} <ArrowRight size={14} />
      </button>
    </div>
    </>
  );
}

function CallbackRequestCard({ contact, quote, total, postcode }) {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/quote/callback-request", {
        name: contact.name, email: contact.email, phone: contact.phone,
        postcode, quote_ref: quote?.reference || "", quote_id: quote?.quote_id || "",
        total, preferred_time: when, message,
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that just now — please give us a call.");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div data-testid="qr-callback-sent" className="mt-4 border border-[#1F6B3B] bg-[#EFF7EE] p-4 text-sm text-[#12263A]">
        <div className="label-eyebrow text-[#1F6B3B] mb-1">Callback request sent</div>
        <p>A member of the team will be in touch within one working day to talk you through the quote. Your reference <b>{quote?.reference}</b> is saved so you can pick up exactly where you left off whenever you&apos;re ready.</p>
      </div>
    );
  }

  return (
    <div data-testid="qr-callback-card" className="mt-4 border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#FBF3EC] text-[#C79A3B] flex items-center justify-center shrink-0"><PhoneCall size={16}/></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-[#12263A]">Not ready to book yet?</div>
          <p className="text-xs text-[#6B7280] mt-0.5">Prefer to talk it through before paying? Ask us to give you a call &mdash; no obligation, no sales script. Your quote &amp; reference stay saved either way.</p>
          {!open ? (
            <button data-testid="qr-callback-open" onClick={() => setOpen(true)} className="de-btn-secondary text-xs mt-3 inline-flex items-center gap-1.5">
              <PhoneCall size={13} /> Ask us to call
            </button>
          ) : (
            <div className="mt-3 space-y-2">
              <label className="label-eyebrow block">Best time to reach you</label>
              <select data-testid="qr-callback-time" value={when} onChange={e => setWhen(e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm">
                <option value="">Any time (Mon–Fri, 9–5)</option>
                <option value="morning">Morning (9am – 12pm)</option>
                <option value="lunchtime">Lunchtime (12pm – 2pm)</option>
                <option value="afternoon">Afternoon (2pm – 5pm)</option>
                <option value="evening">Early evening (5pm – 6:30pm)</option>
              </select>
              <label className="label-eyebrow block mt-2">Anything specific you'd like to discuss? <span className="text-[#9CA3AF] normal-case tracking-normal">(optional)</span></label>
              <textarea data-testid="qr-callback-message" value={message} onChange={e => setMessage(e.target.value)} rows={2}
                placeholder="e.g. can we add a downstairs WC? / start-date flexibility / VAT questions…"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm resize-y" />
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setOpen(false)} disabled={busy} className="text-xs text-[#6B7280] hover:text-[#12263A] px-2 py-1">Cancel</button>
                <button data-testid="qr-callback-submit" onClick={submit} disabled={busy} className="de-btn-primary text-xs">{busy ? "Sending…" : "Send request"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
